import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';

function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-gray-400 hover:text-white mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </button>

        <div className="card-glass mb-8">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 rounded-2xl glass-strong flex items-center justify-center glow-purple">
              <Shield className="w-8 h-8 text-rebel-purple" />
            </div>
            <div>
              <h1 className="text-4xl font-black text-gradient">Privacy Policy</h1>
              <p className="text-gray-400 mt-1">Last updated: January 21, 2026</p>
            </div>
          </div>
          <p className="text-gray-300 leading-relaxed">
            At Rebel Prompts, we take your privacy seriously. This Privacy Policy explains how we collect, use, 
            disclose, and safeguard your information.
          </p>
        </div>

        {/* Content */}
        <div className="card-glass space-y-8">
          <section>
            <h2 className="text-2xl font-bold mb-4 text-rebel-blue-light">1. Information We Collect</h2>
            
            <h3 className="text-xl font-semibold mb-3 text-white">Personal Information</h3>
            <p className="text-gray-300 leading-relaxed mb-3">
              When you create an account, we collect:
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li>Email address</li>
              <li>Name</li>
              <li>Username</li>
              <li>Profile picture (if provided via OAuth)</li>
              <li>Password (encrypted)</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 mt-6 text-white">Usage Data</h3>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li>Prompts you create, edit, and share</li>
              <li>Projects and folders you organize</li>
              <li>Ratings and reviews you provide</li>
              <li>Credit usage and purchase history</li>
              <li>Login activity and session data</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 mt-6 text-white">Automatically Collected</h3>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li>IP address</li>
              <li>Browser type and version</li>
              <li>Device information</li>
              <li>Usage patterns and analytics</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-rebel-blue-light">2. How We Use Your Information</h2>
            <p className="text-gray-300 leading-relaxed mb-3">
              We use your information to:
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li>Provide, operate, and maintain the Service</li>
              <li>Process your transactions and manage subscriptions</li>
              <li>Send you technical notices and support messages</li>
              <li>Respond to your comments and questions</li>
              <li>Improve and personalize your experience</li>
              <li>Monitor usage and prevent abuse</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-rebel-blue-light">3. Data Sharing and Disclosure</h2>
            <p className="text-gray-300 leading-relaxed mb-3">
              We do NOT sell your personal information. We may share data with:
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li><strong className="text-white">Service Providers:</strong> Supabase (database), Stripe (payments), AI providers (OpenAI, Google)</li>
              <li><strong className="text-white">Public Prompts:</strong> Prompts you mark as "public" are visible to all users</li>
              <li><strong className="text-white">Shared Access:</strong> Users you explicitly share prompts with can view your shared content</li>
              <li><strong className="text-white">Legal Requirements:</strong> When required by law or to protect our rights</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-rebel-blue-light">4. Data Security</h2>
            <p className="text-gray-300 leading-relaxed">
              We implement industry-standard security measures including encryption, secure authentication, and regular 
              security audits. However, no method of transmission over the Internet is 100% secure, and we cannot guarantee 
              absolute security.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-rebel-blue-light">5. Your Privacy Rights</h2>
            <p className="text-gray-300 leading-relaxed mb-3">
              You have the right to:
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li>Access your personal data</li>
              <li>Correct inaccurate data</li>
              <li>Request deletion of your data</li>
              <li>Export your data</li>
              <li>Opt-out of marketing communications</li>
              <li>Object to certain data processing</li>
            </ul>
            <p className="text-gray-300 leading-relaxed mt-3">
              To exercise these rights, contact us at{' '}
              <a href="mailto:Support@RebelPrompts.com" className="text-rebel-blue hover:underline">
                Support@RebelPrompts.com
              </a>
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-rebel-blue-light">6. GDPR Compliance</h2>
            <p className="text-gray-300 leading-relaxed">
              For users in the European Economic Area (EEA), we comply with GDPR requirements. Your data is processed 
              based on consent, contractual necessity, and legitimate interests. You have additional rights under GDPR 
              including data portability and the right to lodge a complaint with a supervisory authority.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-rebel-blue-light">7. Cookies and Tracking</h2>
            <p className="text-gray-300 leading-relaxed">
              We use cookies and similar technologies for authentication, preferences, and analytics. You can control 
              cookies through your browser settings, but some features may not function properly if cookies are disabled.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-rebel-blue-light">8. Third-Party Services</h2>
            <p className="text-gray-300 leading-relaxed mb-3">
              Our Service integrates with:
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
                  <li>Email and password authentication</li>
              <li>OpenAI for AI text generation</li>
              <li>Google Gemini for AI image generation</li>
              <li>Stripe for payment processing</li>
              <li>Supabase for data storage</li>
            </ul>
            <p className="text-gray-300 leading-relaxed mt-3">
              These services have their own privacy policies that govern their use of your data.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-rebel-blue-light">9. Data Retention</h2>
            <p className="text-gray-300 leading-relaxed">
              We retain your data for as long as your account is active or as needed to provide services. Upon account 
              deletion, we will delete or anonymize your personal data within 30 days, except where required to retain 
              for legal or accounting purposes.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-rebel-blue-light">10. Children's Privacy</h2>
            <p className="text-gray-300 leading-relaxed">
              Our Service is not intended for children under 13. We do not knowingly collect personal information from 
              children under 13. If you believe we have collected such information, please contact us immediately.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-rebel-blue-light">11. Changes to Privacy Policy</h2>
            <p className="text-gray-300 leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of significant changes via email 
              or prominent notice on the Service. Your continued use after changes indicates acceptance.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-rebel-blue-light">12. Contact Us</h2>
            <p className="text-gray-300 leading-relaxed">
              For privacy-related questions or to exercise your rights, contact us at:
            </p>
            <div className="mt-4 p-4 rounded-xl bg-white/5 border border-white/10">
              <p className="text-white font-semibold">Email:</p>
              <a href="mailto:Support@RebelPrompts.com" className="text-rebel-blue text-lg hover:underline">
                Support@RebelPrompts.com
              </a>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

export default PrivacyPolicy;
